import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Upload, FileText, Image as ImageIcon, Trash2, Download, Loader2, Paperclip } from "lucide-react";

function formatBytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** Blob-based preview URLs so we can display images without token-in-URL. */
function BlobImg({ attachmentId, alt, className }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let created = null;
    api.get(`/sales/attachments/${attachmentId}/download`, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        created = URL.createObjectURL(r.data);
        setUrl(created);
      })
      .catch(() => {});
    return () => { cancelled = true; if (created) URL.revokeObjectURL(created); };
  }, [attachmentId]);
  if (!url) return <div className={`${className} bg-slate-200 animate-pulse`} />;
  return <img src={url} alt={alt} className={className} />;
}

export default function ClientAttachments({ clientId }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/sales/clients/${clientId}/attachments`);
      setItems(data);
    } catch (_) { /* silent */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    let ok = 0;
    for (const f of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        await api.post(`/sales/clients/${clientId}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        ok++;
      } catch (err) {
        toast.error(err.response?.data?.detail || `Failed: ${f.name}`);
      }
    }
    if (ok) toast.success(`Uploaded ${ok} file${ok === 1 ? "" : "s"}`);
    setBusy(false);
    load();
  };

  const openDownload = async (att) => {
    try {
      const r = await api.get(`/sales/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.original_filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (_) { toast.error("Download failed"); }
  };

  const doDelete = async (att) => {
    if (!window.confirm(`Remove "${att.original_filename}"?`)) return;
    try {
      await api.delete(`/sales/attachments/${att.id}`);
      toast.success("Removed");
      load();
    } catch (_) { toast.error("Delete failed"); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(e.dataTransfer.files);
  };

  return (
    <div className="border border-slate-200 rounded-md bg-white shadow-sm" data-testid="client-attachments-card">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 inline-flex items-center gap-2">
          <Paperclip size={14} /> Attachments
        </div>
        <span className="text-xs font-mono text-slate-500">{items.length}</span>
      </div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`m-4 border-2 border-dashed rounded-sm p-6 text-center transition-colors ${dragOver ? "border-blue-800 bg-blue-50" : "border-slate-300 hover:border-slate-400"}`}
        data-testid="attachments-dropzone"
      >
        <Upload size={28} className="mx-auto text-slate-400" />
        <div className="mt-2 text-sm text-slate-700">Drop photos, quotes, floor plans here</div>
        <div className="mt-0.5 text-xs text-slate-500">JPG · PNG · WEBP · HEIC · GIF · PDF · up to 20 MB each</div>
        <Button
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid="attachments-browse-btn"
          className="mt-3 bg-blue-800 hover:bg-blue-900 text-white rounded-sm"
        >
          {busy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Upload size={14} className="mr-1.5" />}
          Browse files
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".jpg,.jpeg,.png,.gif,.webp,.heic,.pdf,image/*,application/pdf"
          onChange={(e) => uploadFiles(e.target.files)}
          className="hidden"
          data-testid="attachments-input"
        />
      </div>
      {items.length > 0 && (
        <ul className="divide-y divide-slate-100">
          {items.map((att) => (
            <li key={att.id} className="px-4 py-3 flex items-center gap-3" data-testid={`attachment-${att.id}`}>
              {att.kind === "image" ? (
                <BlobImg attachmentId={att.id} alt={att.original_filename} className="h-12 w-12 rounded-sm object-cover shrink-0" />
              ) : (
                <div className="h-12 w-12 rounded-sm bg-red-50 border border-red-200 grid place-items-center shrink-0">
                  <FileText size={20} className="text-red-600" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 truncate">{att.original_filename}</div>
                <div className="text-xs text-slate-500 font-mono">
                  {formatBytes(att.size)} · uploaded by {att.uploaded_by_name}
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => openDownload(att)} data-testid={`attachment-download-${att.id}`} className="h-8 w-8 text-slate-600 hover:text-blue-800">
                <Download size={16} />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => doDelete(att)} data-testid={`attachment-delete-${att.id}`} className="h-8 w-8 text-slate-500 hover:text-red-600">
                <Trash2 size={16} />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
